/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useLanguage } from '@/providers/LanguageProvider';
import type { CallBackProps, Step } from 'react-joyride';
import Joyride, { EVENTS, STATUS } from 'react-joyride';

import StackPlus from '/public/logos/tour-guide/stack-plus.svg';

interface GuideProps {
  start: boolean;
  setStartTour: (value: boolean) => void;
  onTourEnd: (finished: boolean) => void;
  hasExampleStep: boolean;
}

interface State {
  run: boolean;
  stepIndex: number;
  steps: Step[];
}

export function Guide({
  start,
  setStartTour,
  onTourEnd,
  hasExampleStep,
}: GuideProps) {
  const [progress, setProgress] = useState<number>(1);
  const kahuChatOpenedRef = useRef(false);
  const { messages } = useLanguage();
  const totalSteps = hasExampleStep ? 4 : 3;

  const generateSteps = (val: number): Step[] => {
    const allSteps: Step[] = [
      {
        content: (
          <div className="relative">
            <div className="flex items-center justify-center rounded-t-lg bg-[#07181E]">
              <Image
                className="py-10"
                src={StackPlus}
                alt={messages?.TopBar?.stack_logo ?? ''}
              />
            </div>
            <div className="flex flex-col gap-y-2 px-5 pt-5">
              <h1 className="text-h3 font-medium">
                {messages?.GuideTour?.title}
              </h1>
              <p
                className="text-body text-body-subtle"
                data-testid="guide-desc"
              >
                {messages?.GuideTour?.guide_desc}
              </p>
            </div>
            <div
              className="absolute -bottom-10 left-5 text-body-sm text-form-input-disabled"
              data-testid="guide-step"
            >
              {val} {messages?.General?.of} {totalSteps}
            </div>
          </div>
        ),
        styles: {
          options: {
            width: 360,
          },
        },
        placement: 'bottom',
        target: '#step-1',
        disableBeacon: true,
        title: '',
      },
      {
        content: (
          <div className="relative">
            <div className="flex items-center justify-center rounded-t-lg bg-[#07181E]">
              <Image
                className="py-10"
                src={StackPlus}
                alt={messages?.TopBar?.stack_logo ?? ''}
              />
            </div>
            <div className="flex flex-col gap-y-2 px-5 pt-5">
              <h1 className="text-h3 font-medium">
                {messages?.GuideTour?.view_tenant_details}
              </h1>
              <p
                className="text-body text-body-subtle"
                data-testid="guide-desc"
              >
                {messages?.GuideTour?.view_tenant_id_and_keys}
              </p>
            </div>
            <div
              className="absolute -bottom-10 left-5 text-body-sm text-form-input-disabled"
              data-testid="guide-step"
            >
              {val} {messages?.General?.of} {totalSteps}
            </div>
          </div>
        ),
        styles: {
          options: {
            width: 360,
          },
        },
        placement: 'right',
        target: '#guide4',
        disableBeacon: true,
        title: '',
      },
      {
        content: (
          <div className="relative">
            <div className="flex items-center justify-center rounded-t-lg bg-[#07181E]">
              <Image
                className="py-10"
                src={StackPlus}
                alt={messages?.TopBar?.stack_logo ?? ''}
              />
            </div>
            <div className="flex flex-col gap-y-2 px-5 pt-5">
              <h1 className="text-h3 font-medium">
                {messages?.GuideTour?.make_okahu_work}
              </h1>
              <p
                className="text-body text-body-subtle"
                data-testid="guide-desc"
              >
                {messages?.GuideTour?.add_new_apps} ....
              </p>
            </div>
            <div
              className="absolute -bottom-10 left-5 text-body-sm text-form-input-disabled"
              data-testid="guide-step"
            >
              {val} {messages?.General?.of} {totalSteps}
            </div>
          </div>
        ),
        styles: {
          options: {
            width: 360,
          },
        },
        placement: 'bottom',
        target: '#step-2',
        disableBeacon: true,
        title: '',
      },
      {
        content: (
          <div className="relative">
            <div className="flex items-center justify-center rounded-t-lg bg-[#07181E]">
              <Image
                className="py-10"
                src="/logos/kahu/kahu-logo.svg"
                alt="kahu-ai-logo"
                width={76.64}
                height={100}
                priority
              />
            </div>
            <div className="flex flex-col gap-y-2 px-5 pt-5">
              <h1 className="text-h3 font-medium">
                {messages?.GuideTour?.ask_kahu_agent_a_question}
              </h1>
              <p
                className="text-body text-body-subtle"
                data-testid="guide-desc"
              >
                {messages?.GuideTour?.kahu_agent_description}
              </p>
            </div>
            <div
              className="absolute -bottom-10 left-5 text-body-sm text-form-input-disabled"
              data-testid="guide-step"
            >
              {val} {messages?.General?.of} {totalSteps}
            </div>
          </div>
        ),
        styles: {
          options: {
            width: 360,
          },
        },
        placement: 'left',
        target: '#kahu-chat',
        disableBeacon: true,
        title: '',
      },
    ];

    return hasExampleStep
      ? allSteps
      : allSteps.filter((s) => s.target !== '#step-1');
  };

  const [{ run, steps, stepIndex }, setState] = useState<State>({
    run: start,
    stepIndex: 0,
    steps: generateSteps(progress),
  });

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      steps: generateSteps(progress),
    }));
  }, [progress]);

  useEffect(() => {
    if (start) {
      kahuChatOpenedRef.current = false;
      setState((prevState) => ({
        ...prevState,
        run: true,
        stepIndex: 0,
      }));
    }
  }, [start]);

  const handleJoyRideCallback = (data: CallBackProps) => {
    const { status, type, index } = data;

    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setState({ steps, run: false, stepIndex: 0 });
      setStartTour(false);
      onTourEnd(status === STATUS.FINISHED);
      kahuChatOpenedRef.current = false;
    } else if (([EVENTS.STEP_AFTER] as string[]).includes(type)) {
      // Advance to the next step
      const nextIndex = index + 1;
      const kahuStepIndex = steps.length - 1;

      if (nextIndex === kahuStepIndex && !kahuChatOpenedRef.current) {
        kahuChatOpenedRef.current = true;

        // Pause tour before the Kahu step
        setState((prev) => ({ ...prev, run: false }));

        // Open the chat window
        window.dispatchEvent(new Event('open-kahu-chat'));

        // Wait for #kahu-chat to mount, then resume at the Kahu step
        setTimeout(() => {
          setState((prev) => ({
            ...prev,
            run: true,
            stepIndex: kahuStepIndex,
          }));
        }, 0);
      } else {
        setState((prev) => ({ ...prev, stepIndex: nextIndex }));
      }

      setProgress(nextIndex + 1);
    }
  };

  return (
    <Joyride
      continuous
      callback={handleJoyRideCallback}
      run={run}
      steps={steps}
      stepIndex={stepIndex}
      scrollToFirstStep
      hideCloseButton
      hideBackButton
      spotlightPadding={5}
      showSkipButton
      locale={{
        skip: messages?.GuideTour?.skip_tour,
        last: messages?.GuideTour?.finish,
      }}
      styles={{
        overlay: {
          background: 'linear-gradient(296.57deg, #021117 0%, #0A2D3A 83.33%)',
        },
        tooltip: {
          padding: '0px',
        },
        tooltipContent: {
          borderRadius: '8px',
          padding: '0px',
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        tooltipFooter: {
          paddingBottom: '20px',
          margin: '20px',
        },
        tooltipFooterSpacer: {
          flex: 'none',
        },
        spotlight: {
          margin: '0px',
          padding: '0px',
          borderRadius: '8px',
        },
        buttonNext: {
          border: '1px solid #34BCAF',
          borderRadius: '2px',
          backgroundColor: '#34BCAF',
          color: '#fff',
          padding: '2px 16px 2px 16px',
          fontSize: '13px',
          lineHeight: '15.25px',
          fontWeight: '500',
        },
        buttonSkip: {
          border: '1px solid white',
          borderRadius: '2px',
          marginRight: '10px',
          color: '#EBEFF1',
          padding: '2px 16px 2px 16px',
          fontSize: '13px',
          lineHeight: '15.25px',
          fontWeight: '500',
        },
        options: {
          zIndex: 100,
          arrowColor: '#40565A',
          backgroundColor: '#0A2D3A',
          textColor: '#fff',
          primaryColor: '#34BCAF',
        },
      }}
    />
  );
}
